// War Room Planning: "Build Draft PO" modal. Shows the rows selected on the Planning table with
// an editable qty per line (defaulting to the existing server draft qty if one exists, else
// mkt_units_mo, else 12), a live extended-cost recompute, and a grand total. "Export CSV" writes
// every line to the server draft (POST /api/war-room/po-draft/bulk) then downloads the CSV via
// the existing GET /api/war-room/po-draft/export endpoint. Matches the app's Headless UI modal
// convention (see components/Purchasing/PODetailModal.js).
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ApiClient from '../../utils/ApiClient';
import FcPinMap from '../Common/FcPinMap';
// Enrichment display rules + pin mapping live in utils/poEnrichment.js, shared with the PO
// Creation page (owner, 2026-09-07: the same data points belong on both surfaces) so the display
// rules can never drift between them.
import { dash, fmtPromise, fmtVelocity, fmtRank, fcPins } from '../../utils/poEnrichment';

const fmtMoney = (v) => (v === null || v === undefined || Number.isNaN(v) ? '—' : `$${v.toFixed(2)}`);

function defaultQty(row) {
    if (row.draft_qty != null && row.draft_qty > 0) return row.draft_qty;
    if (row.mkt_units_mo != null && row.mkt_units_mo > 0) return row.mkt_units_mo;
    return 12;
}

export default function BuildPoModal({ open, onClose, rows, onRemoveLine, onSaved }) {
    const navigate = useNavigate();
    const [qtyByAsin, setQtyByAsin] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedOk, setSavedOk] = useState(false);
    // Enrichment: one batched read per modal open; the hover map renders from this envelope
    // alone (the FC read already carries coordinates), so hovering never fetches.
    const [enrichment, setEnrichment] = useState({});
    const [hoverAsin, setHoverAsin] = useState(null);

    // Re-derive default quantities each time the modal opens (server draft_qty if present, else
    // mkt_units_mo, else 12) — intentionally not re-run on every `rows` change so in-progress edits
    // aren't clobbered while the modal stays open.
    useEffect(() => {
        if (open) {
            const init = {};
            rows.forEach((r) => {
                init[r.asin] = String(defaultQty(r));
            });
            setQtyByAsin(init);
            setError(null);
            setSavedOk(false);
            setEnrichment({});
            setHoverAsin(null);
            const asins = rows.map((r) => r.asin).filter(Boolean);
            if (asins.length > 0) {
                ApiClient.get(`/api/war-room/po-enrichment?asins=${asins.join(',')}`)
                    .then((res) => setEnrichment(res.data?.enrichment || {}))
                    // Enrichment is context, not a gate — a failed read leaves dashes, never
                    // blocks building the PO.
                    .catch((err) => console.error('PO enrichment failed:', err));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const qtyNum = (asin) => {
        const n = parseInt(qtyByAsin[asin], 10);
        return Number.isNaN(n) ? 0 : n;
    };

    const extended = (row) => {
        const cost = Number(row.cost) || 0;
        return qtyNum(row.asin) * cost;
    };

    const totalUnits = rows.reduce((sum, r) => sum + qtyNum(r.asin), 0);
    const totalExtended = rows.reduce((sum, r) => sum + extended(r), 0);

    const handleQtyChange = (asin, value) => {
        setQtyByAsin((prev) => ({ ...prev, [asin]: value }));
    };

    const handleRemove = (asin) => {
        setQtyByAsin((prev) => {
            const next = { ...prev };
            delete next[asin];
            return next;
        });
        onRemoveLine(asin);
    };

    const handleExport = async () => {
        setSaving(true);
        setError(null);
        setSavedOk(false);
        try {
            const qtyMap = {};
            const lines = rows.map((r) => {
                const qty = qtyNum(r.asin);
                qtyMap[r.asin] = qty;
                return { asin: r.asin, qty };
            });

            await ApiClient.post('/api/war-room/po-draft/bulk', { lines });
            if (onSaved) onSaved(qtyMap);
            setSavedOk(true);

            const exportResponse = await ApiClient.get('/api/war-room/po-draft/export', { responseType: 'blob' });
            const blob = new Blob([exportResponse.data], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const dateStr = new Date().toISOString().split('T')[0];
            link.href = url;
            link.download = `po_draft_${dateStr}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error saving/exporting PO draft:', err);
            setError('Failed to save or export the PO draft. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleSendToPlanner = async () => {
        setSaving(true);
        setError(null);
        try {
            const qtyMap = {};
            const lines = rows.map((r) => {
                const qty = qtyNum(r.asin);
                qtyMap[r.asin] = qty;
                return { asin: r.asin, qty };
            });
            // Persist current draft quantities so the Shipment Planner's auto-import (GET
            // /api/war-room/planning -> draft_qty) picks up whatever's on screen right now,
            // including any edits made in this modal that haven't been saved yet.
            await ApiClient.post('/api/war-room/po-draft/bulk', { lines });
            if (onSaved) onSaved(qtyMap);
            navigate('/shipment-planner?mode=asin&fromDraft=1');
        } catch (err) {
            console.error('Error sending draft to Shipment Planner:', err);
            setError('Failed to save the draft before sending to Shipment Planner. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 w-full max-w-7xl">
                                {/* Header */}
                                <div className="bg-gray-50 px-4 py-4 sm:px-6 flex justify-between items-center border-b">
                                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                                        Build Draft PO ({rows.length} {rows.length === 1 ? 'item' : 'items'})
                                    </Dialog.Title>
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                                        onClick={onClose}
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                {error && (
                                    <div className="px-4 py-2 sm:px-6 bg-red-50 border-b border-red-200 text-sm text-red-800">
                                        {error}
                                    </div>
                                )}

                                {/* Line items */}
                                <div className="max-h-[55vh] overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="py-3 pl-4 pr-3 text-left text-xs font-medium text-gray-500 uppercase">Item code</th>
                                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product name</th>
                                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size tier</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Held</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">PO open</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Exp/mo</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase"
                                                    title="Units in Orlando SFP pallet sections (sellable locally)">Local SFP</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase"
                                                    title="Sellable FBA units, latest ledger snapshot — hover the item code for the per-FC map">FBA</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase"
                                                    title="Share of demand seeing a ≤1-day / ≤2-day promise from currently stocked FCs">1d / 2d</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase"
                                                    title="Days with a MAP violation in the last 30 — diagnostic context for the velocity number; cheated-day demand is still real demand">Cheat·30d</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase"
                                                    title="Actual units/day over the last 30 days of orders">Vel</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase"
                                                    title="Current sales rank — read beside actual velocity to spot products underselling their rank">Rank</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit cost</th>
                                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Extended cost</th>
                                                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {rows.map((row) => {
                                                const e = enrichment[row.asin];
                                                const { pins, unknown } = fcPins(e?.fcs);
                                                const hasMap = pins.length > 0 || unknown.length > 0;
                                                return (
                                                <tr key={row.asin}>
                                                    {/* Enter on the code text; leave on the CELL — the popover is a DOM child of the
                                                        cell, so moving the cursor into the map keeps it open (mouseleave ignores
                                                        moves onto descendants). */}
                                                    <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm text-gray-900 relative"
                                                        onMouseLeave={() => setHoverAsin((prev) => (prev === row.asin ? null : prev))}>
                                                        <span className={hasMap ? 'underline decoration-dotted cursor-help' : ''}
                                                              onMouseEnter={() => hasMap && setHoverAsin(row.asin)}>
                                                            {row.item_code || '—'}
                                                        </span>
                                                        {hoverAsin === row.asin && hasMap && (
                                                            <div className="absolute left-0 top-full z-30 w-[32rem] shadow-xl border border-gray-200 rounded-md">
                                                                <FcPinMap pins={pins} unknown={unknown} heightPx={200}
                                                                          selectedLabel="Selected plans" />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-3 text-sm text-gray-900 max-w-[16rem] truncate" title={row.title || ''}>
                                                        {row.title || '—'}
                                                        <div className="text-xs text-gray-400 font-mono">{row.asin}</div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                                                        {row.size_tier || '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                                                        {row.our_units ?? '—'}
                                                    </td>
                                                    {/* Owner, 2026-09-07: hovering on-order names the POs behind the number. */}
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right"
                                                        title={row.po_open_numbers ? `Open PO(s): ${row.po_open_numbers}` : undefined}>
                                                        {row.po_open_qty ?? '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                                                        {row.mkt_units_mo ?? '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-700 text-right">
                                                        {e ? dash(e.localSfpUnits, String) : '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-700 text-right">
                                                        {e ? dash(e.fbaStock, String) : '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-700 text-right">
                                                        {fmtPromise(e)}
                                                    </td>
                                                    <td className={`whitespace-nowrap px-3 py-3 text-sm text-right ${
                                                        (e?.cheatedDays30 || 0) > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
                                                        {e ? dash(e.cheatedDays30, String) : '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-700 text-right">
                                                        {e ? fmtVelocity(e.velocityPerDay) : '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                                                        {e ? fmtRank(e.salesRank) : '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-right">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={qtyByAsin[row.asin] ?? ''}
                                                            onChange={(e) => handleQtyChange(row.asin, e.target.value)}
                                                            className="w-20 text-sm text-right border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                        />
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                                                        {fmtMoney(Number(row.cost))}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-900 text-right font-medium">
                                                        {fmtMoney(extended(row))}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemove(row.asin)}
                                                            aria-label={`Remove ${row.asin}`}
                                                            className="text-gray-400 hover:text-red-600"
                                                        >
                                                            <XMarkIcon className="h-4 w-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                            {rows.length === 0 && (
                                                <tr>
                                                    <td colSpan="16" className="py-8 text-center text-gray-500">
                                                        No rows selected.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        {rows.length > 0 && (
                                            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                                                <tr>
                                                    <td colSpan="12" className="px-3 py-3 text-sm font-semibold text-gray-700 text-right">
                                                        Totals
                                                    </td>
                                                    <td className="px-3 py-3 text-sm font-semibold text-gray-900 text-right">
                                                        {totalUnits.toLocaleString()}
                                                    </td>
                                                    <td></td>
                                                    <td className="px-3 py-3 text-sm font-semibold text-gray-900 text-right">
                                                        {fmtMoney(totalExtended)}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>

                                {/* Footer */}
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-between items-center border-t">
                                    <span className="text-sm text-gray-500">
                                        {savedOk && !error ? 'Saved to draft.' : ''}
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            disabled={saving || rows.length === 0}
                                            onClick={handleSendToPlanner}
                                            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                                        >
                                            {saving ? 'Saving…' : 'Send to Shipment Planner'}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={saving || rows.length === 0}
                                            onClick={handleExport}
                                            className="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50"
                                        >
                                            {saving ? 'Saving…' : 'Export CSV'}
                                        </button>
                                        <button
                                            type="button"
                                            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                            onClick={onClose}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
